import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaCopy, FaExternalLinkAlt, FaCoins, FaCreditCard, FaGasPump, FaLink, FaClock } from 'react-icons/fa';

const TransactionSuccessModal = ({ isOpen, onClose, transactionData }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  if (!isOpen || !transactionData) return null;

  const {
    type,
    crypto,
    amount,
    payment,
    gas,
    txHash,
    timestamp,
    success = true,
    error
  } = transactionData;

  const handleCopyHash = async () => {
    try {
      await navigator.clipboard.writeText(txHash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
      alert('Error al copiar el hash');
    }
  };

  const handleViewOnBSC = () => {
    window.open(`https://bscscan.com/tx/${txHash}`, '_blank');
  };

  const formatDate = (ts) => {
    const date = new Date(ts);
    return date.toLocaleString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const modalContentStyle = {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '550px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    animation: 'slideDown 0.4s ease-out',
    position: 'relative'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
  };

  const iconStyle = {
    fontSize: '4rem',
    marginBottom: '15px',
    color: success ? '#4caf50' : '#f44336',
    filter: 'drop-shadow(0 0 20px currentColor)'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#fff',
    margin: '10px 0',
    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
  };

  const sectionStyle = {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '15px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease'
  };

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.9rem',
    color: '#aaa',
    marginBottom: '8px',
    fontWeight: '500'
  };

  const valueStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#fff',
    marginLeft: '30px'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '10px',
    marginTop: '25px',
    flexWrap: 'wrap'
  };

  const buttonStyle = {
    flex: 1,
    minWidth: '120px',
    padding: '12px 20px',
    borderRadius: '10px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  };

  const hashContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '10px',
    flexWrap: 'wrap'
  };

  const hashStyle = {
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    color: '#64b5f6',
    wordBreak: 'break-all',
    flex: 1
  };

  const iconButtonStyle = {
    padding: '8px 15px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(100, 181, 246, 0.2)',
    color: '#64b5f6',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '0.85rem',
    fontWeight: '600'
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#fff',
    fontSize: '1.5rem',
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  };

  const timestampStyle = {
    textAlign: 'center',
    color: '#888',
    fontSize: '0.9rem',
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .modal-section:hover {
          background: rgba(255, 255, 255, 0.08) !important;
          transform: translateX(5px);
        }
        .icon-button:hover {
          background: rgba(100, 181, 246, 0.3) !important;
          transform: scale(1.05);
        }
        .primary-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6) !important;
        }
        .secondary-button:hover {
          background: rgba(255, 255, 255, 0.15) !important;
        }
        .close-button:hover {
          background: rgba(244, 67, 54, 0.3) !important;
          transform: rotate(90deg);
        }
      `}</style>
      
      <div style={modalOverlayStyle} onClick={onClose}>
        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
          <button 
            style={closeButtonStyle} 
            onClick={onClose}
            className="close-button"
          >
            ×
          </button>

          <div style={headerStyle}>
            <div style={iconStyle}>
              {success ? <FaCheckCircle /> : <FaTimesCircle />}
            </div>
            <h2 style={titleStyle}>
              {success 
                ? (type === 'compra' ? '¡Compra Exitosa!' : '¡Venta Exitosa!')
                : '¡Transacción Fallida!'
              }
            </h2>
            {!success && error && (
              <div style={{ 
                background: 'rgba(244, 67, 54, 0.1)', 
                border: '1px solid #f44336', 
                borderRadius: '8px', 
                padding: '15px', 
                marginTop: '15px',
                color: '#ff6b6b',
                fontSize: '0.95rem'
              }}>
                <strong>Error:</strong> {error}
              </div>
            )}
          </div>

          {/* Sección: Cantidad */}
          <div style={sectionStyle} className="modal-section">
            <div style={labelStyle}>
              <FaCoins style={{ color: '#ffd700', fontSize: '1.2rem' }} />
              <span>{type === 'compra' ? 'Compraste' : 'Vendiste'}</span>
            </div>
            <div style={valueStyle}>
              {parseFloat(amount).toFixed(4)} {crypto}
            </div>
          </div>

          {/* Sección: Pago/Recibo */}
          <div style={sectionStyle} className="modal-section">
            <div style={labelStyle}>
              <FaCreditCard style={{ color: type === 'compra' ? '#f44336' : '#4caf50', fontSize: '1.2rem' }} />
              <span>{type === 'compra' ? 'Pagaste con' : 'Recibiste'}</span>
            </div>
            <div style={valueStyle}>
              {payment.currency === 'CLP' 
                ? `$${parseFloat(payment.amount).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CLP`
                : `${parseFloat(payment.amount).toFixed(2)} ${payment.currency}`
              }
            </div>
          </div>

          {/* Sección: Gas */}
          {gas && gas.bnb > 0 && gas.clp > 0 && (
            <div style={sectionStyle} className="modal-section">
              <div style={labelStyle}>
                <FaGasPump style={{ color: '#ff9800', fontSize: '1.2rem' }} />
                <span>Gas Consumido</span>
              </div>
              <div style={valueStyle}>
                {parseFloat(gas.bnb).toFixed(6)} BNB
              </div>
              <div style={{ marginLeft: '30px', color: '#aaa', fontSize: '0.9rem', marginTop: '5px' }}>
                (~${Math.round(parseFloat(gas.clp)).toLocaleString('es-CL')} CLP)
              </div>
            </div>
          )}

          {/* Sección: Hash */}
          {txHash && (
            <div style={sectionStyle} className="modal-section">
              <div style={labelStyle}>
                <FaLink style={{ color: '#64b5f6', fontSize: '1.2rem' }} />
                <span>Hash de Transacción</span>
              </div>
              <div style={hashContainerStyle}>
                <div style={hashStyle}>
                  {txHash.substring(0, 10)}...{txHash.substring(txHash.length - 8)}
                </div>
                <button 
                  style={iconButtonStyle}
                  onClick={handleCopyHash}
                  className="icon-button"
                >
                  <FaCopy /> {copied ? '¡Copiado!' : 'Copiar'}
                </button>
                <button 
                  style={iconButtonStyle}
                  onClick={handleViewOnBSC}
                  className="icon-button"
                >
                  <FaExternalLinkAlt /> BSCScan
                </button>
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div style={timestampStyle}>
            <FaClock />
            {formatDate(timestamp)}
          </div>

          {/* Botones de acción */}
          <div style={buttonContainerStyle}>
            <button 
              style={secondaryButtonStyle}
              onClick={onClose}
              className="secondary-button"
            >
              Cerrar
            </button>
            <button 
              style={primaryButtonStyle}
              onClick={() => {
                onClose();
                navigate('/billetera/balance');
              }}
              className="primary-button"
            >
              Ver Historial
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionSuccessModal;
